import { HoverPopover } from '@app/components/base/HoverPopover';
import { UploadedPlatformsStatus } from '@app/modules/social-export/components/UploadedPlatformsStatus';
import { ContentAiIcon } from '@app/modules/content-ai/ContentAiIcon';
import {
  getDisplayPlatformItems,
  DisplayPlatformItem,
} from '@app/modules/social-export/lib/get-display-platform-items';
import {
  ExternalDeliveryStatusDto,
  UploadStatusItemDto,
} from '@app/modules/fragments/fragment.types';
import { theme as appTheme } from '@mi-front/ui';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { Box, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

const DEFAULT_MAX_VISIBLE_ITEMS = 4;

/** Icon sizes in px: pill (inline) and popover (expanded). */
const ICON_SIZES = {
  small: { pill: 16, popover: 24 },
  medium: { pill: 24, popover: 32 },
} as const;

export type FragmentCardStatusesSize = 'small' | 'medium';

/** Unified platform status item from either uploadStatuses or publishedPlatforms. */
export type PlatformStatusItem = DisplayPlatformItem;

type StatusItem =
  | { type: 'upload'; item: PlatformStatusItem }
  | { type: 'external' }
  | { type: 'contentAi' };

const SocialIconWrapper = ({
  children,
  size = 24,
}: {
  children: React.ReactNode;
  size?: number;
}) => (
  <Box
    sx={{
      width: size,
      height: size,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& svg': { fontSize: size * 0.9 },
    }}
  >
    {children}
  </Box>
);

export type FragmentCardStatusesProps = {
  uploadStatuses: UploadStatusItemDto[];
  externalDelivery?: ExternalDeliveryStatusDto;
  /** When non-empty, used instead of uploadStatuses for platform icons. */
  publishedPlatforms?: ReadonlyArray<{
    platform: string;
    status: string;
    uploadId: string;
  }>;
  /** When set, shows external delivery icon (takes precedence over externalDelivery.enabled). */
  externalDeliveryStartedAt?: string;
  /** When set, shows Content AI created icon. */
  contentAiCreatedAt?: string;
  /** Icon size: small (default) or medium for bigger icons. */
  size?: FragmentCardStatusesSize;
  /** Max items shown inline before "+N" and tooltip. Default 4. Ignored when wrap is true. */
  maxVisibleItems?: number;
  /** Show unpublished status. Default false. */
  showUnpublished?: boolean;
};

/**
 * Returns true when FragmentCardStatuses would render something (status icons,
 * external delivery icon, Content AI icon, or "Sin publicar" text). Use to avoid wrapping in a
 * pill when there is nothing to show.
 */
export function hasStatusesContent(props: {
  uploadStatuses: UploadStatusItemDto[];
  externalDelivery?: ExternalDeliveryStatusDto;
  publishedPlatforms?: ReadonlyArray<{
    platform: string;
    status: string;
    uploadId: string;
  }>;
  externalDeliveryStartedAt?: string;
  contentAiCreatedAt?: string;
  showUnpublished?: boolean;
}): boolean {
  const {
    uploadStatuses,
    externalDelivery,
    publishedPlatforms,
    externalDeliveryStartedAt,
    contentAiCreatedAt,
    showUnpublished = false,
  } = props;
  const displayPlatformItems = getDisplayPlatformItems({ uploadStatuses, publishedPlatforms });
  const showExternalDelivery =
    externalDeliveryStartedAt != null || externalDelivery?.enabled === true;
  const showContentAi = contentAiCreatedAt != null;
  return (
    displayPlatformItems.length > 0 ||
    showExternalDelivery ||
    showContentAi ||
    (showUnpublished &&
      displayPlatformItems.length === 0 &&
      !showExternalDelivery &&
      !showContentAi)
  );
}

/**
 * Renders platform icons for fragment upload statuses and external delivery.
 * Completed uploads use green, SCHEDULED/FAILED use orange. Only COMPLETED, SCHEDULED
 * and FAILED statuses are shown. When external delivery is enabled, shows a green
 * upload icon. For grid overlay, wrap in FragmentMediaPill at the call site.
 */
export function FragmentCardStatuses({
  uploadStatuses,
  externalDelivery,
  publishedPlatforms,
  externalDeliveryStartedAt,
  contentAiCreatedAt,
  size: sizeProp = 'small',
  maxVisibleItems = DEFAULT_MAX_VISIBLE_ITEMS,
  showUnpublished = false,
}: FragmentCardStatusesProps) {
  const { formatMessage } = useIntl();
  const displayPlatformItems = getDisplayPlatformItems({ uploadStatuses, publishedPlatforms });
  const showExternalDelivery =
    externalDeliveryStartedAt != null || externalDelivery?.enabled === true;
  const showContentAi = contentAiCreatedAt != null;

  if (
    displayPlatformItems.length === 0 &&
    !showExternalDelivery &&
    !showContentAi &&
    showUnpublished
  ) {
    return (
      <Typography variant="caption" sx={{ color: 'text.disabled' }}>
        {formatMessage({
          id: 'fragments.listScreen.unpublished',
          defaultMessage: 'Sin publicar',
        })}
      </Typography>
    );
  }

  const iconSizes = ICON_SIZES[sizeProp];
  const allItems: StatusItem[] = [
    ...displayPlatformItems.map((item) => ({ type: 'upload' as const, item })),
    ...(showExternalDelivery ? [{ type: 'external' as const }] : []),
    ...(showContentAi ? [{ type: 'contentAi' as const }] : []),
  ];
  const visibleItems = allItems.slice(0, maxVisibleItems);
  const remainingCount = allItems.length - maxVisibleItems;

  const statusContent = (
    <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.25}>
      {visibleItems.map((statusItem) => {
        if (statusItem.type === 'external') {
          return (
            <Box key="external">
              <SocialIconWrapper size={iconSizes.pill}>
                <FileUploadIcon sx={{ color: appTheme.greenDark }} />
              </SocialIconWrapper>
            </Box>
          );
        }
        if (statusItem.type === 'contentAi') {
          return (
            <Box key="contentAi">
              <SocialIconWrapper size={iconSizes.pill}>
                <ContentAiIcon sx={{ color: appTheme.greenDark }} />
              </SocialIconWrapper>
            </Box>
          );
        }

        return (
          <UploadedPlatformsStatus
            key={statusItem.item.id}
            variant="pill"
            size={sizeProp}
            items={[statusItem.item]}
          />
        );
      })}

      {remainingCount > 0 && (
        <Typography component="span" variant="caption" sx={{ lineHeight: 1 }} color="#ddd">
          +{remainingCount}
        </Typography>
      )}
    </Box>
  );

  const popoverContent = (
    <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
      <UploadedPlatformsStatus variant="popover" size={sizeProp} items={displayPlatformItems} />
      {showExternalDelivery && (
        <Box>
          <SocialIconWrapper size={iconSizes.popover}>
            <FileUploadIcon sx={{ color: appTheme.greenDark }} />
          </SocialIconWrapper>
        </Box>
      )}
      {showContentAi && (
        <Box>
          <SocialIconWrapper size={iconSizes.popover}>
            <ContentAiIcon sx={{ color: appTheme.greenDark }} />
          </SocialIconWrapper>
        </Box>
      )}
    </Box>
  );

  const isTruncated = remainingCount > 0;

  if (isTruncated) {
    return <HoverPopover trigger={statusContent}>{popoverContent}</HoverPopover>;
  }

  return statusContent;
}
